import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { ScanLine, Loader2, Sparkles, UploadCloud, FileText, CheckCircle2 } from "lucide-react";
import PageHead from "@/components/PageHead";
import { MatchRing, CountUp } from "@/components/ui-bits";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function ATSChecker() {
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [resume, setResume] = useState("");
  const [jd, setJd] = useState("");
  const [fileName, setFileName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const loadProfile = () => {
    if (user?.profile) {
      const p = user.profile;
      const skillsStr = Array.isArray(p.skills) ? p.skills.join(", ") : p.skills || "Product Strategy, Analytics";
      const formatted = `${user.name || "Candidate"} — ${p.current_role || "Product Specialist"}
Experience: ${p.years_experience || 3}+ years in ${p.current_role || "Product Management"}.
Preferred Work Model: ${p.work_model || "Remote"}.
Target Comp: ${p.target_salary || "$140k"}.
Key Skills & Competencies: ${skillsStr}.
Summary: Proven track record driving product discovery, cross-functional stakeholder alignment, and data-informed roadmap delivery.`;
      setResume(formatted);
      setFileName("Career_Profile.txt");
      toast.success("Loaded Career Profile CV data");
    } else {
      toast("No saved Career Profile found yet");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);

    if (file.name.endsWith(".txt") || file.name.endsWith(".md")) {
      try {
        const text = await file.text();
        if (text.trim()) {
          setResume(text);
          setFileName(file.name);
          toast.success(`Loaded ${file.name}`);
          setUploading(false);
          if (fileInputRef.current) fileInputRef.current.value = "";
          return;
        }
      } catch (_) {}
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const { data } = await api.post("/ai/parse-resume", formData);
      if (data?.text) {
        setResume(data.text);
        setFileName(file.name);
        toast.success(`Extracted text from ${file.name}`);
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not parse CV file.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const analyze = async () => {
    if (!resume.trim()) {
      toast.error("Please upload or paste your resume first.");
      return;
    }
    if (!jd.trim()) {
      toast.error("Please enter the target Job Description.");
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post("/ai/ats-analyze", {
        resume_text: resume,
        job_description: jd,
      });
      setResult(data);
      toast.success("ATS analysis complete!");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "ATS analysis failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const r = result;
  const bars = r
    ? [
        ["Keywords", r.keywords_score || 75],
        ["Skills", r.skills_score || 80],
        ["Experience", r.experience_score || 85],
        ["Formatting", r.formatting_score || 90],
        ["JD alignment", r.jd_alignment_score || 78],
      ]
    : [];

  return (
    <div>
      <PageHead
        label="ATS Checker"
        title="More than an ATS score"
        sub="See exactly how applicant tracking algorithms and AI recruiters read your CV against any job description."
      />

      <div className="grid gap-6 lg:grid-cols-2 items-start">
        <div className="space-y-4">
          <div className="card-surface p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <label className="label-caps flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5 text-electric" /> Your Resume / CV
              </label>
              <button
                type="button"
                onClick={loadProfile}
                className="text-xs font-semibold text-electric hover:underline"
              >
                Use Career Profile
              </button>
            </div>

            <div className="mb-3">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".pdf,.docx,.txt"
                className="hidden"
                id="ats-cv-upload"
              />
              <label
                htmlFor="ats-cv-upload"
                className="flex cursor-pointer items-center justify-between rounded-xl border border-dashed border-border bg-secondary/40 px-4 py-2.5 text-xs font-medium transition-colors hover:border-electric hover:bg-secondary"
              >
                <div className="flex items-center gap-2">
                  {uploading ? (
                    <Loader2 className="h-4 w-4 animate-spin text-electric" />
                  ) : (
                    <UploadCloud className="h-4 w-4 text-electric" />
                  )}
                  <span>
                    {uploading
                      ? "Extracting CV with AI…"
                      : fileName
                      ? `Loaded: ${fileName}`
                      : "Upload CV (.pdf, .docx, .txt)"}
                  </span>
                </div>
                <span className="rounded-md bg-card px-2.5 py-1 text-[11px] font-semibold border border-border shadow-xs">
                  Browse
                </span>
              </label>
            </div>

            <textarea
              data-testid="ats-resume"
              value={resume}
              onChange={(e) => setResume(e.target.value)}
              rows={8}
              placeholder="Paste or upload your resume text here…"
              className="w-full resize-none rounded-xl border border-border bg-background p-3 text-xs leading-relaxed outline-none focus:border-electric font-mono"
            />
          </div>

          <div className="card-surface p-5 shadow-sm">
            <label className="label-caps block mb-2">Job description</label>
            <textarea
              data-testid="ats-jd"
              value={jd}
              onChange={(e) => setJd(e.target.value)}
              rows={6}
              placeholder="Paste the target job description or requirements here…"
              className="w-full resize-none rounded-xl border border-border bg-background p-3 text-sm leading-relaxed outline-none focus:border-electric"
            />
          </div>

          <button
            onClick={analyze}
            disabled={loading || uploading}
            data-testid="ats-analyze"
            className="cv-btn-primary w-full py-3 text-sm font-semibold"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Analyzing ATS Compatibility…
              </>
            ) : (
              <>
                <ScanLine className="h-4 w-4" /> Run Live ATS Check
              </>
            )}
          </button>
        </div>

        <div>
          {!r && (
            <div className="card-surface flex h-full min-h-[380px] flex-col items-center justify-center rounded-3xl border border-dashed border-border p-8 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-electric/10 text-electric mb-3">
                <Sparkles className="h-7 w-7" />
              </div>
              <h3 className="font-heading text-lg font-bold">Run an Analysis</h3>
              <p className="mt-1 max-w-sm text-xs text-muted-foreground leading-relaxed">
                Upload your CV and paste the job description to get machine scoring, missing keyword gaps, and optimization advice.
              </p>
            </div>
          )}

          {r && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
              <div className="card-surface flex items-center gap-6 p-6 shadow-sm">
                <MatchRing
                  value={r.score || 82}
                  size={110}
                  stroke={10}
                  color="hsl(var(--chart-1))"
                  label={<CountUp to={r.score || 82} />}
                />
                <div>
                  <p className="font-heading text-lg font-bold">Overall ATS Score</p>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{r.summary}</p>
                </div>
              </div>

              <div className="card-surface space-y-3 p-5 shadow-sm">
                {bars.map(([l, v]) => (
                  <div key={l}>
                    <div className="flex justify-between text-xs font-semibold">
                      <span>{l}</span>
                      <span className="text-electric font-bold">{v}%</span>
                    </div>
                    <div className="mt-1.5 h-2 rounded-full bg-secondary">
                      <motion.div
                        className="h-full rounded-full bg-gradient-to-r from-electric to-aqua"
                        initial={{ width: 0 }}
                        animate={{ width: `${v}%` }}
                        transition={{ duration: 0.8 }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="card-surface p-4 shadow-sm">
                  <p className="label-caps mb-2 text-lime flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Matched Keywords
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {(r.matched_keywords || []).map((k) => (
                      <span
                        key={k}
                        className="rounded-full bg-lime/10 border border-lime/20 px-2.5 py-1 text-xs font-semibold text-lime-600 dark:text-lime"
                      >
                        {k}
                      </span>
                    ))}
                    {(!r.matched_keywords || r.matched_keywords.length === 0) && (
                      <span className="text-xs text-muted-foreground">None identified</span>
                    )}
                  </div>
                </div>

                <div className="card-surface p-4 shadow-sm">
                  <p className="label-caps mb-2 text-coral">Missing Keywords</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(r.missing_keywords || []).map((k) => (
                      <span
                        key={k}
                        className="rounded-full bg-coral/10 border border-coral/20 px-2.5 py-1 text-xs font-semibold text-coral"
                      >
                        {k}
                      </span>
                    ))}
                    {(!r.missing_keywords || r.missing_keywords.length === 0) && (
                      <span className="text-xs text-muted-foreground">None</span>
                    )}
                  </div>
                </div>
              </div>

              {r.recommendations && (
                <div className="card-surface p-5 shadow-sm">
                  <p className="label-caps mb-2.5 text-electric">AI Optimization Advice</p>
                  <ul className="space-y-1.5 text-xs text-muted-foreground">
                    {r.recommendations.map((rec, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <span className="text-electric font-bold">→</span>
                        <span>{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
